// NewOrderPage.jsx — single-page order creation flow
//
// This is the V2 flow. The legacy 4-step wizard is preserved verbatim at
// /order/new/legacy (NewOrderPageLegacy.js) as a fallback.
//
// Goals:
//  - Everything on one page (client, products, details, discount/tax, payments, totals).
//  - Fast keyboard loop: client → product → qty → price → Add → product (no mouse needed).
//  - Reuse all order-only logic from the legacy page (inventory, payments, draft, tax cache,
//    debt update, conditional config gates) so the backend payload is byte-compatible.
//  - Isolated localStorage keys (V2) so legacy and new pages don't clobber each other.
//  - Inline product creation, inline inventory adjust dialog.
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Container, Typography, Box, TextField, Button, CircularProgress, Alert,
  Card, CardHeader, CardContent, Divider, Paper, IconButton, Tooltip,
  Autocomplete, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  InputAdornment, FormControlLabel, Checkbox, MenuItem, Select, FormControl, InputLabel,
  Radio, RadioGroup, Chip,
  DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import heLocale from 'date-fns/locale/he';
import { useNavigate, useLocation } from 'react-router-dom';
import API from '../services/api';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useUser } from '../contexts/UserContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';

import ClientCreateDialog from './dialogs/ClientCreateDialog';
import MobileDialog from './dialogs/MobileDialog';
import InventoryManagementDialog from './dialogs/InventoryManagementDialog';
import ProductCreateInline from './components/order/ProductCreateInline';
import PaymentsSection from './components/order/PaymentsSection';
import BarcodeScannerDialog from './dialogs/BarcodeScannerDialog';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import PersonIcon from '@mui/icons-material/Person';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import WarningIcon from '@mui/icons-material/Warning';
import HistoryIcon from '@mui/icons-material/History';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';

const theme = createTheme({ direction: 'rtl' });

const DRAFT_TTL_MS = 18 * 60 * 60 * 1000; // 18 hours

const numberInputSx = {
  '& .MuiInputBase-root': { direction: 'ltr' },
  '& input': {
    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
    MozAppearance: 'textfield',
  },
};

const fetchClients = async (searchTerm = '') => {
  const params = { light: true, limit: 100 };
  if (searchTerm) params.search = searchTerm;
  const { data } = await API.get('/clients/', { params });
  return data;
};

const fetchProducts = async () => {
  const { data } = await API.get('/products/');
  return data;
};

// MUI Autocomplete's default filterOptions only matches against the
// label returned by getOptionLabel (ProductName + CatalogNumber).
// Barcode isn't in the label, so typing a barcode would get
// filtered out client-side. Match across every searchable field.
const productFilterOptions = (options, { inputValue }) => {
  const q = (inputValue || '').trim().toLowerCase();
  if (!q) return options;
  return options.filter((opt) => {
    const hay = [opt.ProductName, opt.CatalogNumber, opt.Barcode, opt.ProductID]
      .filter((v) => v != null && v !== '')
      .map((v) => String(v).toLowerCase());
    return hay.some((s) => s.includes(q));
  });
};

const fetchProductDetails = async (productId) => {
  const { data } = await API.get('/product/', { params: { ProductID: productId } });
  return data;
};

const formatCurrency = (n) =>
  new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(n || 0);

const deserializeDraft = (raw) => {
  const draft = JSON.parse(raw);
  if (draft.formData) {
    ['OrderDate', 'DueDate'].forEach((k) => {
      if (draft.formData[k]) draft.formData[k] = new Date(draft.formData[k]);
    });
    draft.formData.payments?.forEach((p) => {
      if (p.checkDate) p.checkDate = new Date(p.checkDate);
      if (p.transferDate) p.transferDate = new Date(p.transferDate);
    });
  }
  return draft;
};

const NewOrderPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { userProfile } = useUser();

  const queryParams = new URLSearchParams(location.search);
  const preselectedClientId = queryParams.get('clientId');

  // -- Config (mirrors legacy) --
  const uiConfig = userProfile?.ui_config || {};
  const orderFields = uiConfig.order_fields || [];
  const generalSchema = uiConfig?.orders_general_configs?.general_schema || {};
  const productLabel = generalSchema?.product?.label || 'מוצר';
  const productPluralLabel = generalSchema?.product?.plural_label || 'מוצרים';
  const orderLabel = generalSchema?.order?.label || 'הזמנה';
  const newOrderLabel = generalSchema?.new_order?.label || 'הזמנה חדשה';
  const allowOrderManagement = uiConfig.allow_order_management || false;
  const orderConfig = userProfile?.group?.order_config || {};
  const storageConfig = orderConfig.storage_config || {};
  const storageEnabled = storageConfig.enabled && allowOrderManagement;
  const barcodeEnabled = userProfile?.group?.barcode_config?.enabled === true;
  const productFields = uiConfig.product_fields || [];
  const productFieldsSchema = uiConfig.product_fields_schema || {};

  // Reuse the ProductsPage inventory role keys so order-driven inventory
  // changes follow the same gate as the manual surfaces. UI-only; when
  // the user lacks role, we show a Hebrew prompt telling them to get a
  // manager instead of opening the inventory dialog.
  const role = userProfile?.role;
  const productsPageConfig = uiConfig.pages_configs?.ProductsPage || {};
  const canTakeInventory = (productsPageConfig.inventory_take_roles || ['manager', 'employee']).includes(role);
  const canAddInventory = (productsPageConfig.inventory_add_roles || ['manager', 'employee']).includes(role);
  const canAdjustInventory = canTakeInventory || canAddInventory;

  const orderFieldEnabled = (name) => orderFields.length === 0 || orderFields.includes(name);

  // -- V2 localStorage keys (isolated from legacy) --
  const userId = userProfile?.username || 'unknown';
  const TAX_STATE_KEY = `orderTaxStateV2-${userId}`;
  const ROUNDING_KEY = `orderRoundingEnabledV2-${userId}`;
  const DRAFT_KEY = `orderDraftV2-${userId}`;
  // Session-scoped flag: once the user has been prompted (or dismissed, or
  // started working), don't reshow the restore dialog again in this tab even
  // if they navigate away and come back. Cleared when the browser tab closes.
  const DRAFT_PROMPTED_KEY = `orderDraftV2Prompted-${userId}`;

  // -- Form state --
  const [formData, setFormData] = useState({
    ClientBusinessID: '',
    ClientName: '',
    OrderDate: new Date(),
    DueDate: null,
    Status: 'pending',
    TotalAmount: 0,
    Subtotal: 0,
    CalculatedDiscount: 0,
    DiscountAmount: '',
    DiscountPercentage: '',
    DiscountReason: '',
    PaidAmount: 0,
    InvoiceNumber: '',
    Comments: '',
    payments: [],
    IsTaxable: false,
    IsTaxIncluded: false,
    TaxRate: 18.0,
    TaxAmount: 0,
    NetAmount: 0,
  });

  const [selectedClient, setSelectedClient] = useState(null);
  const [productEntries, setProductEntries] = useState([]);
  const [discountType, setDiscountType] = useState('none'); // none, amount, percentage
  const [taxState, setTaxState] = useState('none'); // none, included, excluded
  const [roundingEnabled, setRoundingEnabled] = useState(false);

  // -- Inline add-product row state --
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productQuantity, setProductQuantity] = useState('');
  const [productUnitPrice, setProductUnitPrice] = useState('');
  const [selectedStorage, setSelectedStorage] = useState(null);
  const [availableStorages, setAvailableStorages] = useState([]);
  const [noInventoryWarning, setNoInventoryWarning] = useState(false);

  // -- Dialog state --
  const [createClientDialogOpen, setCreateClientDialogOpen] = useState(false);
  const [createProductDialogOpen, setCreateProductDialogOpen] = useState(false);
  const [inventoryAdjustOpen, setInventoryAdjustOpen] = useState(false);

  // Role-aware opener — same Hebrew fallback as the order detail flow.
  const requestInventoryAdjust = () => {
    if (!canAdjustInventory) {
      setProductsCardError('יש לפנות למנהל לעדכון המלאי');
      return;
    }
    setInventoryAdjustOpen(true);
  };
  const [draftDialogOpen, setDraftDialogOpen] = useState(false);
  const [pendingDraft, setPendingDraft] = useState(null);
  const [scannerOpen, setScannerOpen] = useState(false);

  // -- Submit / error --
  const [formError, setFormError] = useState('');
  const [productsCardError, setProductsCardError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // -- Refs for keyboard loop --
  const clientAutocompleteRef = useRef(null);
  const productAutocompleteRef = useRef(null);
  const quantityRef = useRef(null);
  const unitPriceRef = useRef(null);
  const addButtonRef = useRef(null);

  // -- Refs for auto-scroll on errors / warnings --
  const errorAlertRef = useRef(null);
  const lowStockAlertRef = useRef(null);

  // -- Sync ref to guarantee single-shot submit even on rapid double-click --
  const isSubmittingRef = useRef(false);

  // -- Debounced client search --
  const [debouncedAutocompleteSearch, setDebouncedAutocompleteSearch] = useState('');
  const autocompleteTimerRef = useRef(null);
  const debouncedSetAutocompleteSearch = useCallback((value) => {
    if (autocompleteTimerRef.current) clearTimeout(autocompleteTimerRef.current);
    autocompleteTimerRef.current = setTimeout(() => {
      setDebouncedAutocompleteSearch(value);
    }, 300);
  }, []);

  // -- Queries --
  const { data: clients = [], isLoading: isLoadingClients, isError: isErrorClients } = useQuery({
    queryKey: ['clients', 'autocomplete', debouncedAutocompleteSearch],
    queryFn: () => fetchClients(debouncedAutocompleteSearch),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const { data: products = [], isError: isErrorProducts } = useQuery({
    queryKey: ['products'],
    queryFn: fetchProducts,
    staleTime: 5 * 60 * 1000,
  });

  const { data: allStorages = [] } = useQuery({
    queryKey: ['storages'],
    queryFn: async () => (await API.get('/storages/')).data,
    enabled: storageEnabled,
    staleTime: 5 * 60 * 1000,
  });

  // -- Create order mutation --
  const createOrderMutation = useMutation({
    mutationFn: async (orderData) => (await API.post('/order/create/', orderData)).data,
    onSuccess: (data) => {
      setFormSuccess(`${orderLabel} נוצרה בהצלחה`);
      queryClient.invalidateQueries(['orders']);
      localStorage.removeItem(DRAFT_KEY);
      sessionStorage.removeItem(DRAFT_PROMPTED_KEY);
      setTimeout(() => navigate(`/order/${data.OrderID}`, { state: { from: '/orders' } }), 800);
    },
    onError: (error) => {
      setFormError(error?.response?.data?.error || `שגיאה ביצירת ${orderLabel}`);
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    },
  });

  // -- Effects --

  // Load tax/rounding cache (V2 keys)
  useEffect(() => {
    const savedTax = localStorage.getItem(TAX_STATE_KEY);
    if (savedTax && ['none', 'included', 'excluded'].includes(savedTax)) {
      setTaxState(savedTax);
      setFormData((prev) => ({
        ...prev,
        IsTaxable: savedTax !== 'none',
        IsTaxIncluded: savedTax === 'included',
      }));
    }
    const savedRounding = localStorage.getItem(ROUNDING_KEY);
    if (savedRounding) setRoundingEnabled(savedRounding === 'true');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset scroll + focus on mount. Without this, returning to /order/new from
  // a deep page (or selecting a preselected client) leaves the viewport on
  // the lower half of the form. We always want the user to see the top card
  // and have the cursor on the first relevant input.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    if (!preselectedClientId) {
      // preventScroll keeps the page anchored at the top instead of scrolling
      // the focused input into view (which on mobile pushes the page header off-screen).
      setTimeout(() => clientAutocompleteRef.current?.focus({ preventScroll: true }), 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Draft detection on mount — only fires once per browser tab session and
  // only if the user has nothing in-progress already.
  useEffect(() => {
    try {
      // Already prompted in this tab session? Skip — we don't want the dialog
      // popping up again every time the user navigates back to /order/new.
      if (sessionStorage.getItem(DRAFT_PROMPTED_KEY)) return;
      // User already has work in progress? Don't interrupt them.
      if (selectedClient || productEntries.length > 0) return;

      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const draft = deserializeDraft(raw);
      if (Date.now() - draft.savedAt > DRAFT_TTL_MS) {
        localStorage.removeItem(DRAFT_KEY);
        return;
      }
      // Mark as prompted before opening so a re-mount can't double-fire.
      sessionStorage.setItem(DRAFT_PROMPTED_KEY, '1');
      setPendingDraft(draft);
      setDraftDialogOpen(true);
    } catch (e) {
      console.warn('Failed to load order draft:', e);
      localStorage.removeItem(DRAFT_KEY);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pre-select client from ?clientId=
  useEffect(() => {
    if (preselectedClientId && clients.length > 0 && !selectedClient) {
      const c = clients.find((x) => x.ClientBusinessID === preselectedClientId);
      if (c) {
        setSelectedClient(c);
        setFormData((prev) => ({
          ...prev,
          ClientBusinessID: c.ClientBusinessID,
          ClientName: c.ClientName,
        }));
        setTimeout(() => productAutocompleteRef.current?.focus({ preventScroll: true }), 100);
      }
    }
  }, [preselectedClientId, clients, selectedClient]);

  // Recalculate totals
  useEffect(() => {
    const subtotal = productEntries.reduce((sum, entry) => {
      const price = parseFloat(entry.customPrice ?? entry.product.Price) || 0;
      const qty = parseFloat(entry.quantity) || 0;
      return sum + price * qty;
    }, 0);

    let discountAmount = 0;
    if (discountType === 'amount') {
      discountAmount = Math.round((parseFloat(formData.DiscountAmount) || 0) * 100) / 100;
    } else if (discountType === 'percentage') {
      const pct = Math.round((parseFloat(formData.DiscountPercentage) || 0) * 100) / 100;
      discountAmount = Math.round(subtotal * (pct / 100) * 100) / 100;
    }
    const afterDiscount = Math.round((subtotal - discountAmount) * 100) / 100;

    let totalAmount, taxAmount, netAmount;
    const taxRate = formData.TaxRate || 18.0;
    if (taxState === 'none') {
      netAmount = afterDiscount;
      taxAmount = 0;
      totalAmount = afterDiscount;
    } else if (taxState === 'included') {
      totalAmount = afterDiscount;
      netAmount = Math.round((afterDiscount / (1 + taxRate / 100)) * 100) / 100;
      taxAmount = Math.round((totalAmount - netAmount) * 100) / 100;
    } else {
      netAmount = afterDiscount;
      taxAmount = Math.round(afterDiscount * (taxRate / 100) * 100) / 100;
      totalAmount = Math.round((netAmount + taxAmount) * 100) / 100;
    }

    let finalTotal = totalAmount;
    let roundingAdjustment = 0;
    if (roundingEnabled) {
      const rounded = Math.floor(totalAmount);
      roundingAdjustment = rounded - totalAmount;
      finalTotal = rounded;
    }

    setFormData((prev) => ({
      ...prev,
      Subtotal: Math.round(subtotal * 100) / 100,
      // Effective discount in ₪ for both discount types. Kept separate from
      // DiscountAmount, which is the user's fixed-amount input.
      CalculatedDiscount: discountAmount,
      NetAmount: netAmount,
      TaxAmount: taxAmount,
      TotalAmount: finalTotal,
      RoundingAdjustment: roundingAdjustment,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productEntries, formData.DiscountAmount, formData.DiscountPercentage, discountType, taxState, formData.TaxRate, roundingEnabled]);

  // Auto-save draft (V2 key)
  useEffect(() => {
    if (formSuccess || isSubmitting) return;
    if (!selectedClient && productEntries.length === 0) return;
    const t = setTimeout(() => {
      try {
        const draft = {
          savedAt: Date.now(),
          formData,
          selectedClient,
          productEntries,
          discountType,
          taxState,
          roundingEnabled,
        };
        localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
      } catch (e) {
        console.warn('Failed to save order draft:', e);
      }
    }, 1000);
    return () => clearTimeout(t);
  }, [formSuccess, isSubmitting, formData, selectedClient, productEntries, discountType, taxState, roundingEnabled, DRAFT_KEY]);

  // Auto-scroll to error / warning when one appears so the user is never
  // surprised by a silently failed action that's offscreen.
  useEffect(() => {
    if (formError && errorAlertRef.current) {
      errorAlertRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [formError]);

  useEffect(() => {
    if ((noInventoryWarning || productsCardError) && lowStockAlertRef.current) {
      lowStockAlertRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [noInventoryWarning, productsCardError]);

  // Recompute PaidAmount from payments
  useEffect(() => {
    const totalPaid = formData.payments.reduce((s, p) => s + parseFloat(p.amount || 0), 0);
    setFormData((prev) => ({ ...prev, PaidAmount: totalPaid }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.payments]);

  // -- Handlers --

  const handleClientSelect = (event, newValue) => {
    if (newValue && typeof newValue === 'object' && !newValue.isNoResults) {
      setSelectedClient(newValue);
      setFormData((prev) => ({
        ...prev,
        ClientBusinessID: newValue.ClientBusinessID,
        ClientName: newValue.ClientName,
      }));
      setTimeout(() => productAutocompleteRef.current?.focus({ preventScroll: true }), 100);
    } else if (!newValue) {
      setSelectedClient(null);
      setFormData((prev) => ({ ...prev, ClientBusinessID: '', ClientName: '' }));
    }
  };

  const handleProductSelect = async (_, newValue) => {
    if (!newValue) {
      setSelectedProduct(null);
      setProductUnitPrice('');
      setAvailableStorages([]);
      setSelectedStorage(null);
      setNoInventoryWarning(false);
      return;
    }

    setNoInventoryWarning(false);
    setSelectedProduct(newValue);

    if (storageEnabled) {
      try {
        const full = await fetchProductDetails(newValue.ProductID);
        setSelectedProduct(full);
        setProductUnitPrice(String(full.Price ?? ''));
        if (full.inventory && full.inventory.length > 0) {
          const withStock = full.inventory.filter((i) => i.available_quantity > 0);
          if (withStock.length === 0) {
            setNoInventoryWarning(true);
            setAvailableStorages([]);
            setSelectedStorage(null);
          } else {
            setAvailableStorages(withStock);
            const def = withStock.find((i) => i.is_default) || withStock[0];
            setSelectedStorage(def);
          }
        } else {
          setNoInventoryWarning(true);
          setAvailableStorages([]);
          setSelectedStorage(null);
        }
      } catch (e) {
        setProductsCardError('שגיאה בטעינת פרטי המוצר');
      }
    } else {
      setProductUnitPrice(String(newValue.Price ?? ''));
    }

    if (!productQuantity) setProductQuantity('1');
    setTimeout(() => {
      quantityRef.current?.focus();
      quantityRef.current?.select?.();
    }, 150);
  };

  // Shared add/merge logic — used by both the manual "Add product" button
  // and the barcode scanner. Returns an error string on validation failure,
  // or '' on success.
  const appendOrMergeEntry = useCallback((product, qty, price, storage) => {
    if (!product) return 'מוצר חסר';
    if (!qty || qty <= 0) return 'נא להזין כמות';
    if (storageEnabled && storage && qty > storage.available_quantity) {
      return `הכמות המבוקשת (${qty}) עולה על המלאי הזמין (${storage.available_quantity})`;
    }
    const resolvedPrice = Number.isFinite(price) ? price : Number(product.Price) || 0;
    const isCustom = Math.abs(resolvedPrice - Number(product.Price)) > 0.001;

    setProductEntries((prev) => {
      const existingIdx = prev.findIndex((entry) => {
        const sameProduct = entry.product.ProductID === product.ProductID;
        const samePrice =
          (!entry.customPrice && !isCustom) ||
          (entry.customPrice && isCustom && Math.abs(Number(entry.customPrice) - resolvedPrice) < 0.001);
        const sameStorage = storageEnabled
          ? entry.storageId === (storage?.StorageID || null)
          : true;
        return sameProduct && samePrice && sameStorage;
      });

      if (existingIdx >= 0) {
        const updated = [...prev];
        updated[existingIdx] = {
          ...updated[existingIdx],
          quantity: Math.round((parseFloat(updated[existingIdx].quantity) + qty) * 100) / 100,
        };
        return updated;
      }
      return [
        ...prev,
        {
          product,
          quantity: Math.round(qty * 100) / 100,
          customPrice: isCustom ? resolvedPrice : null,
          storageId: storage?.StorageID || null,
          storageName: storage?.StorageName || null,
          entryId: Date.now(),
        },
      ];
    });
    return '';
  }, [storageEnabled]);

  const handleAddProductRow = () => {
    if (!selectedProduct) return;
    setProductsCardError('');
    if (storageEnabled && noInventoryWarning) {
      setProductsCardError(`לא ניתן להוסיף ${productLabel} ללא מלאי. יש לעדכן את המלאי תחילה.`);
      return;
    }
    const qty = parseFloat(productQuantity);
    const price = parseFloat(productUnitPrice) || selectedProduct.Price;

    const err = appendOrMergeEntry(selectedProduct, qty, price, selectedStorage);
    if (err) {
      setProductsCardError(err);
      return;
    }

    setSelectedProduct(null);
    setProductQuantity('');
    setProductUnitPrice('');
    setSelectedStorage(null);
    setAvailableStorages([]);
    setNoInventoryWarning(false);
    setFormError('');
    setProductsCardError('');
    setTimeout(() => productAutocompleteRef.current?.focus(), 50);
  };

  // Barcode scanner callback: hydrate the product (for inventory data),
  // pick a sensible default storage, then add via the shared helper.
  const handleScannedProductAdd = async ({ product, quantity: qty, unitPrice: price }) => {
    if (!product) return;
    setProductsCardError('');
    let full = product;
    let storage = null;

    if (storageEnabled) {
      try {
        full = await fetchProductDetails(product.ProductID);
      } catch (e) {
        setProductsCardError('שגיאה בטעינת פרטי המוצר');
        return;
      }
      const withStock = (full.inventory || []).filter((i) => i.available_quantity > 0);
      if (withStock.length === 0) {
        setProductsCardError(`לא ניתן להוסיף ${productLabel} ללא מלאי. יש לעדכן את המלאי תחילה.`);
        return;
      }
      storage = withStock.find((i) => i.is_default) || withStock[0];
    }

    const err = appendOrMergeEntry(full, qty, price, storage);
    if (err) {
      setProductsCardError(err);
    }
  };

  const handleRemoveProductEntry = (idx) => {
    setProductEntries((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleUpdateEntry = (idx, field, value) => {
    setProductEntries((prev) =>
      prev.map((entry, i) => {
        if (i !== idx) return entry;
        if (field === 'quantity') {
          return { ...entry, quantity: parseFloat(value) || 0 };
        }
        if (field === 'customPrice') {
          const newPrice = parseFloat(value);
          if (isNaN(newPrice)) return entry;
          const isCustom = Math.abs(newPrice - Number(entry.product.Price)) > 0.001;
          return { ...entry, customPrice: isCustom ? newPrice : null };
        }
        return entry;
      })
    );
  };

  const handleTaxStateChange = (e) => {
    const v = e.target.value;
    setTaxState(v);
    localStorage.setItem(TAX_STATE_KEY, v);
    setFormData((prev) => ({
      ...prev,
      IsTaxable: v !== 'none',
      IsTaxIncluded: v === 'included',
    }));
  };

  const handleRoundingChange = (e) => {
    const enabled = e.target.checked;
    setRoundingEnabled(enabled);
    localStorage.setItem(ROUNDING_KEY, enabled.toString());
  };

  const handleDiscountTypeChange = (e) => {
    const v = e.target.value;
    setDiscountType(v);
    if (v === 'none') {
      setFormData((prev) => ({
        ...prev,
        DiscountAmount: '',
        DiscountPercentage: '',
        DiscountReason: '',
      }));
    }
  };

  const handleRestoreDraft = () => {
    if (!pendingDraft) return;
    setFormData(pendingDraft.formData);
    setSelectedClient(pendingDraft.selectedClient);
    setProductEntries(pendingDraft.productEntries || []);
    setDiscountType(pendingDraft.discountType || 'none');
    setTaxState(pendingDraft.taxState || 'none');
    setRoundingEnabled(pendingDraft.roundingEnabled || false);
    setDraftDialogOpen(false);
    setPendingDraft(null);
  };

  const handleDiscardDraft = () => {
    localStorage.removeItem(DRAFT_KEY);
    setDraftDialogOpen(false);
    setPendingDraft(null);
  };

  const handleProductCreated = (enhancedProduct, defaultStorage) => {
    setSelectedProduct(enhancedProduct);
    setProductUnitPrice(String(enhancedProduct.Price ?? ''));
    if (storageEnabled) {
      const withStock = (enhancedProduct.inventory || []).filter(
        (i) => i.available_quantity > 0
      );
      if (withStock.length > 0) {
        setAvailableStorages(withStock);
        setSelectedStorage(
          defaultStorage || withStock.find((i) => i.is_default) || withStock[0]
        );
        setNoInventoryWarning(false);
      } else {
        // The user created the product without seeding any inventory — fire
        // the warning so they can bump it inline via InventoryManagementDialog.
        setAvailableStorages([]);
        setSelectedStorage(null);
        setNoInventoryWarning(true);
      }
    }
    queryClient.invalidateQueries(['products']);
    if (!productQuantity) setProductQuantity('1');
    setTimeout(() => quantityRef.current?.focus(), 150);
  };

  const handleInventoryAdjusted = (refreshedProduct) => {
    setSelectedProduct(refreshedProduct);
    if (refreshedProduct.inventory && refreshedProduct.inventory.length > 0) {
      const withStock = refreshedProduct.inventory.filter((i) => i.available_quantity > 0);
      if (withStock.length > 0) {
        setAvailableStorages(withStock);
        // Prefer to keep the previously selected storage if it still has stock
        // (so the qty-overage error gets cleared correctly), otherwise default.
        const samePicked = selectedStorage
          ? withStock.find((i) => i.StorageID === selectedStorage.StorageID)
          : null;
        const def = samePicked || withStock.find((i) => i.is_default) || withStock[0];
        setSelectedStorage(def);
        setNoInventoryWarning(false);
      }
    }
    // Clear any product-card error (e.g. "qty exceeds available") since the
    // user just bumped inventory to fix it.
    setProductsCardError('');
    setTimeout(() => quantityRef.current?.focus(), 100);
  };

  const handleSubmit = () => {
    // Sync ref blocks the second click before React re-renders the disabled state.
    if (isSubmittingRef.current || isSubmitting || createOrderMutation.isLoading) return;
    isSubmittingRef.current = true;

    setFormError('');
    if (!formData.ClientBusinessID) {
      setFormError('יש לבחור לקוח');
      isSubmittingRef.current = false;
      return;
    }
    if (productEntries.length === 0) {
      setFormError(`יש להוסיף לפחות ${productLabel} אחד ${orderLabel}`);
      isSubmittingRef.current = false;
      return;
    }

    setIsSubmitting(true);

    const orderData = {
      ClientBusinessID: formData.ClientBusinessID,
      OrderDate: format(formData.OrderDate, 'yyyy-MM-dd'),
      Status: formData.Status,
      TotalAmount: Math.round(formData.TotalAmount * 100) / 100,
      Subtotal: Math.round(formData.Subtotal * 100) / 100,
      Notes: formData.Comments || '',
      items: productEntries.map((entry) => ({
        ProductID: entry.product.ProductID,
        Quantity: Math.round(entry.quantity * 100) / 100,
        UnitPrice: Math.round((parseFloat(entry.customPrice ?? entry.product.Price) || 0) * 100) / 100,
        ...(storageEnabled && entry.storageId && { StorageID: entry.storageId }),
      })),
    };

    if (formData.payments.length > 0) {
      orderData.payments = formData.payments.map((payment) => {
        const p = {
          Amount: payment.amount,
          PaymentMethod: payment.method,
          Notes: payment.notes || '',
        };
        if (payment.method === 'check') {
          if (payment.checkNumber) p.CheckNumber = payment.checkNumber;
          if (payment.checkDate) p.CheckDate = format(new Date(payment.checkDate), 'yyyy-MM-dd');
          if (payment.checkBankName) p.CheckBankName = payment.checkBankName;
          if (payment.checkAccountNumber) p.CheckAccountNumber = payment.checkAccountNumber;
        } else if (payment.method === 'credit_card') {
          if (payment.cardLastFour) p.CardLastFour = payment.cardLastFour;
          if (payment.cardType) p.CardType = payment.cardType;
        } else if (payment.method === 'bank_transfer') {
          if (payment.bankName) p.BankName = payment.bankName;
          if (payment.bankAccountLast4) p.BankAccountLast4 = payment.bankAccountLast4;
          if (payment.transferDate) p.TransferDate = format(new Date(payment.transferDate), 'yyyy-MM-dd');
        }
        return p;
      });
    }

    if (formData.DueDate) orderData.DueDate = format(formData.DueDate, 'yyyy-MM-dd');
    if (formData.InvoiceNumber) orderData.InvoiceNumber = formData.InvoiceNumber;

    if (discountType === 'amount' && formData.DiscountAmount) {
      const v = Math.round((parseFloat(formData.DiscountAmount) || 0) * 100) / 100;
      if (v > 0) {
        orderData.DiscountAmount = v;
        if (formData.DiscountReason) orderData.DiscountReason = formData.DiscountReason;
      }
    } else if (discountType === 'percentage' && formData.DiscountPercentage) {
      const v = Math.round((parseFloat(formData.DiscountPercentage) || 0) * 100) / 100;
      if (v > 0) {
        orderData.DiscountPercentage = v;
        if (formData.DiscountReason) orderData.DiscountReason = formData.DiscountReason;
      }
    }

    orderData.IsTaxable = formData.IsTaxable;
    orderData.IsTaxIncluded = formData.IsTaxIncluded;
    orderData.TaxRate = Math.round((formData.TaxRate || 18.0) * 100) / 100;
    orderData.RoundingEnabled = roundingEnabled;

    createOrderMutation.mutate(orderData);
  };

  const clientAutocompleteOptions = useMemo(() => {
    if (clients.length === 0 && debouncedAutocompleteSearch) {
      return [{ ClientBusinessID: -1, ClientName: 'אין תוצאות', isNoResults: true }];
    }
    // Make sure the currently-selected client stays in the options list even
    // when it's not part of the latest server response (avoids a noisy MUI
    // "value does not match any option" warning).
    if (selectedClient && !clients.some((c) => c.ClientBusinessID === selectedClient.ClientBusinessID)) {
      return [selectedClient, ...clients];
    }
    return clients;
  }, [clients, debouncedAutocompleteSearch, selectedClient]);

  // -- Render --
  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={heLocale}>
        <Container maxWidth="lg" sx={{ py: 3, px: { xs: 1, sm: 2 } }} dir="rtl">
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h5" fontWeight="bold">
                <ShoppingCartIcon sx={{ mr: 1, verticalAlign: 'middle', direction: 'ltr' }} />
                {newOrderLabel}
              </Typography>
              <Tooltip title="חזרה לדף הישן">
                <Button
                  size="small"
                  variant="text"
                  startIcon={<HistoryIcon />}
                  onClick={() => navigate('/order/new/legacy' + (location.search || ''))}
                  sx={{ direction: 'ltr' }}
                >
                  דף ישן
                </Button>
              </Tooltip>
            </Box>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => {
                // Context-aware back: if the user came from a client page
                // (?clientId=...) return to that client's detail page; otherwise
                // fall back to the orders list.
                if (preselectedClientId) {
                  navigate(`/client/${preselectedClientId}`);
                } else {
                  navigate('/orders');
                }
              }}
              sx={{ direction: 'ltr' }}
            >
              {preselectedClientId ? 'חזרה ללקוח' : 'חזרה לרשימה'}
            </Button>
          </Box>

          {(isErrorClients || isErrorProducts) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              שגיאה בטעינת נתונים. אנא רענן את הדף ונסה שוב.
            </Alert>
          )}
          {formError && (
            <Alert
              ref={errorAlertRef}
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setFormError('')}
            >
              {formError}
            </Alert>
          )}
          {formSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>{formSuccess}</Alert>
          )}

          {/* Client Card */}
          <Card sx={{ mb: 3 }}>
            <CardHeader
              title={
                <Typography variant="h6">
                  <PersonIcon sx={{ mr: 1, verticalAlign: 'middle', direction: 'ltr' }} />
                  בחירת לקוח
                </Typography>
              }
            />
            <CardContent>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Autocomplete
                  fullWidth
                  options={clientAutocompleteOptions}
                  value={selectedClient}
                  getOptionLabel={(o) => {
                    if (typeof o === 'string') return o;
                    if (o?.isNoResults) return o.ClientName;
                    return o ? `${o.ClientName} (${o.ClientBusinessID})` : '';
                  }}
                  getOptionDisabled={(o) => o?.isNoResults === true}
                  isOptionEqualToValue={(o, v) => o?.ClientBusinessID === v?.ClientBusinessID}
                  filterOptions={(x) => x}
                  loading={isLoadingClients}
                  noOptionsText="הקלד לחיפוש לקוח"
                  onChange={handleClientSelect}
                  onInputChange={(_, value) => debouncedSetAutocompleteSearch(value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="חיפוש לקוח"
                      placeholder="הקלד שם או ח.פ..."
                      inputRef={clientAutocompleteRef}
                    />
                  )}
                />
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={() => setCreateClientDialogOpen(true)}
                  sx={{ direction: 'ltr', whiteSpace: 'nowrap' }}
                >
                  לקוח חדש
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Order details card */}
          <Card sx={{ mb: 3 }}>
            <CardHeader title={<Typography variant="h6">פרטי {orderLabel}</Typography>} />
            <CardContent>
              {/* Compact fields: fixed widths on one wrapping row (desktop), notes on
                  their own full-width row below; 2-column grid on phones. */}
              <Box
                sx={{
                  display: { xs: 'grid', sm: 'flex' },
                  gridTemplateColumns: '1fr 1fr',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                {orderFieldEnabled('OrderDate') && (
                  <DatePicker
                    label="תאריך הזמנה"
                    value={formData.OrderDate}
                    onChange={(d) => setFormData((p) => ({ ...p, OrderDate: d || new Date() }))}
                    format="dd/MM/yyyy"
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: {
                          width: { xs: '100%', sm: 170 },
                          minWidth: { xs: 0, sm: 170 },
                          '& .MuiInputBase-root': { direction: 'ltr' },
                        },
                      },
                    }}
                  />
                )}
                {orderFieldEnabled('DueDate') && (
                  <DatePicker
                    label="תאריך אספקה"
                    value={formData.DueDate}
                    onChange={(d) => setFormData((p) => ({ ...p, DueDate: d }))}
                    format="dd/MM/yyyy"
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: {
                          width: { xs: '100%', sm: 170 },
                          minWidth: { xs: 0, sm: 170 },
                          '& .MuiInputBase-root': { direction: 'ltr' },
                        },
                      },
                    }}
                  />
                )}
                {orderFieldEnabled('InvoiceNumber') && (
                  <TextField
                    size="small" label="מספר חשבונית"
                    value={formData.InvoiceNumber}
                    onChange={(e) => setFormData((p) => ({ ...p, InvoiceNumber: e.target.value }))}
                    sx={{ width: { xs: '100%', sm: 150 }, minWidth: { xs: 0, sm: 150 } }}
                  />
                )}
                {orderFieldEnabled('Status') && (
                  <FormControl size="small" sx={{ width: { xs: '100%', sm: 130 }, minWidth: { xs: 0, sm: 130 } }}>
                    <InputLabel>סטטוס</InputLabel>
                    <Select
                      label="סטטוס"
                      value={formData.Status}
                      onChange={(e) => setFormData((p) => ({ ...p, Status: e.target.value }))}
                    >
                      <MenuItem value="pending">ממתין</MenuItem>
                      <MenuItem value="completed">הושלם</MenuItem>
                      <MenuItem value="cancelled">בוטל</MenuItem>
                    </Select>
                  </FormControl>
                )}
                <TextField
                  size="small"
                  label="הערות"
                  placeholder={`הוסף הערות ל${orderLabel} (אופציונלי)`}
                  value={formData.Comments}
                  onChange={(e) => setFormData((p) => ({ ...p, Comments: e.target.value }))}
                  sx={{ gridColumn: '1 / -1', flexBasis: '100%' }}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Discount / Tax / Rounding card */}
          <Card sx={{ mb: 3 }}>
            <CardHeader
              sx={{ pb: 0 }}
              title={<Typography variant="h6">הנחה, מע"מ ועיגול</Typography>}
            />
            <CardContent>
              {/* Compact single row on desktop: discount group | VAT group | rounding,
                  separated by vertical dividers. Stacks with horizontal dividers on phones. */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  alignItems: { xs: 'stretch', md: 'center' },
                  gap: 2,
                }}
              >
                {/* Discount group */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, minWidth: 0 }}>
                  <FormControl size="small" sx={{ width: { xs: '100%', sm: 150 }, minWidth: { xs: 0, sm: 150 } }}>
                    <InputLabel>הנחה</InputLabel>
                    <Select
                      value={discountType}
                      label="הנחה"
                      onChange={handleDiscountTypeChange}
                    >
                      <MenuItem value="none">ללא הנחה</MenuItem>
                      <MenuItem value="percentage">אחוז הנחה</MenuItem>
                      <MenuItem value="amount">סכום קבוע</MenuItem>
                    </Select>
                  </FormControl>
                  {discountType === 'percentage' && (
                    <TextField
                      size="small" type="number" label="אחוז הנחה"
                      value={formData.DiscountPercentage}
                      onChange={(e) => setFormData((p) => ({ ...p, DiscountPercentage: e.target.value }))}
                      onFocus={(e) => e.target.select()}
                      onWheel={(e) => e.target.blur()}
                      sx={{ width: { xs: '100%', sm: 130 }, minWidth: { xs: 0, sm: 130 }, ...numberInputSx }}
                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                    />
                  )}
                  {discountType === 'amount' && (
                    <TextField
                      size="small" type="number" label="סכום הנחה"
                      value={formData.DiscountAmount}
                      onChange={(e) => setFormData((p) => ({ ...p, DiscountAmount: e.target.value }))}
                      onFocus={(e) => e.target.select()}
                      onWheel={(e) => e.target.blur()}
                      sx={{ width: { xs: '100%', sm: 130 }, minWidth: { xs: 0, sm: 130 }, ...numberInputSx }}
                      InputProps={{ endAdornment: <InputAdornment position="end">₪</InputAdornment> }}
                    />
                  )}
                  {discountType !== 'none' && (
                    <TextField
                      size="small" label="סיבת הנחה"
                      value={formData.DiscountReason}
                      onChange={(e) => setFormData((p) => ({ ...p, DiscountReason: e.target.value }))}
                      sx={{ width: { xs: '100%', sm: 180 }, minWidth: { xs: 0, sm: 180 } }}
                    />
                  )}
                </Box>

                <Divider flexItem orientation="vertical" sx={{ display: { xs: 'none', md: 'block' } }} />
                <Divider sx={{ display: { xs: 'block', md: 'none' } }} />

                {/* VAT group */}
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1.5 }}>
                  <Typography variant="subtitle2" color="text.secondary">מע"מ:</Typography>
                  <RadioGroup row value={taxState} onChange={handleTaxStateChange}>
                    <FormControlLabel value="none" control={<Radio size="small" />} label="ללא" />
                    <FormControlLabel value="included" control={<Radio size="small" />} label='כולל' />
                    <FormControlLabel value="excluded" control={<Radio size="small" />} label='לא כולל' />
                  </RadioGroup>
                  {taxState !== 'none' && (
                    <Typography variant="caption" color="text.secondary">
                      ({formData.TaxRate}%)
                    </Typography>
                  )}
                </Box>

                <Divider flexItem orientation="vertical" sx={{ display: { xs: 'none', md: 'block' } }} />
                <Divider sx={{ display: { xs: 'block', md: 'none' } }} />

                {/* Rounding */}
                <FormControlLabel
                  sx={{ m: 0, whiteSpace: 'nowrap' }}
                  control={<Checkbox size="small" checked={roundingEnabled} onChange={handleRoundingChange} />}
                  label="עיגול למטה"
                />
              </Box>
            </CardContent>
          </Card>

          {/* Products Card */}
          <Card sx={{ mb: 3 }}>
            <CardHeader title={<Typography variant="h6">הוספת {productPluralLabel}</Typography>} />
            <CardContent>
              {/* Card-scoped alerts always render at the TOP of the card so the
                  user sees them immediately, before the input row. Layout:
                  message on top, action button on its own row right-aligned —
                  no Alert close-X to compete with the CTA visually. */}
              {productsCardError && (
                <Alert
                  ref={lowStockAlertRef}
                  severity="error"
                  sx={{
                    mb: 2,
                    '& .MuiAlert-message': { width: '100%', py: 1 },
                  }}
                >
                  <Typography variant="body2" sx={{ mb: storageEnabled && selectedProduct && selectedStorage ? 1.5 : 0 }}>
                    {productsCardError}
                  </Typography>
                  {/* If we have a product + storage in scope, the most likely
                      recovery is to bump inventory in that storage — surface
                      the same CTA the no-stock warning uses. */}
                  {storageEnabled && selectedProduct && selectedStorage && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 1 }}>
                      <Button
                        color="error"
                        size="small"
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => requestInventoryAdjust()}
                        sx={{ direction: 'ltr', whiteSpace: 'nowrap' }}
                      >
                        עדכן מלאי
                      </Button>
                      <Button
                        color="inherit"
                        size="small"
                        variant="text"
                        onClick={() => setProductsCardError('')}
                        sx={{ direction: 'ltr', whiteSpace: 'nowrap' }}
                      >
                        סגור
                      </Button>
                    </Box>
                  )}
                </Alert>
              )}

              {storageEnabled && noInventoryWarning && selectedProduct && (
                <Alert
                  ref={!productsCardError ? lowStockAlertRef : undefined}
                  severity="warning"
                  icon={<WarningIcon />}
                  sx={{
                    mb: 2,
                    '& .MuiAlert-message': { width: '100%', py: 1 },
                  }}
                >
                  <Typography variant="body2" sx={{ mb: 1.5 }}>
                    אין מלאי זמין ל<strong>{selectedProduct?.ProductName || productLabel}</strong>.
                    לחץ על הכפתור כדי להוסיף מלאי במחסן ולהמשיך.
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                    <Button
                      color="warning"
                      size="small"
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => requestInventoryAdjust()}
                      sx={{ direction: 'ltr', whiteSpace: 'nowrap' }}
                    >
                      עדכן מלאי
                    </Button>
                  </Box>
                </Alert>
              )}

              <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <Autocomplete
                  sx={{ flex: 2, minWidth: 220 }}
                  options={products}
                  value={selectedProduct}
                  onChange={handleProductSelect}
                  getOptionLabel={(o) =>
                    o ? `${o.ProductName}${o.CatalogNumber ? ` (${o.CatalogNumber})` : ''}` : ''
                  }
                  filterOptions={productFilterOptions}
                  isOptionEqualToValue={(o, v) => o?.ProductID === v?.ProductID}
                  noOptionsText={`לא נמצאו ${productPluralLabel}`}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={`חיפוש ${productLabel}`}
                      size="small"
                      inputRef={productAutocompleteRef}
                    />
                  )}
                />
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setCreateProductDialogOpen(true)}
                  sx={{ direction: 'ltr', whiteSpace: 'nowrap', alignSelf: 'center' }}
                >
                  {`${productLabel} חדש`}
                </Button>
                {barcodeEnabled && (
                  <Tooltip title="סריקת ברקוד">
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      startIcon={<QrCodeScannerIcon />}
                      onClick={() => setScannerOpen(true)}
                      sx={{ direction: 'ltr', whiteSpace: 'nowrap', alignSelf: 'center' }}
                    >
                      סרוק
                    </Button>
                  </Tooltip>
                )}
                <TextField
                  label="כמות"
                  type="number"
                  size="small"
                  inputRef={quantityRef}
                  value={productQuantity}
                  onChange={(e) => setProductQuantity(e.target.value)}
                  onFocus={(e) => e.target.select()}
                  onWheel={(e) => e.target.blur()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      unitPriceRef.current?.focus();
                      unitPriceRef.current?.select?.();
                    }
                  }}
                  sx={{
                    minWidth: 90,
                    '& input': {
                      '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                        WebkitAppearance: 'none', margin: 0,
                      },
                      MozAppearance: 'textfield',
                    },
                  }}
                  inputProps={{ min: 0.01, step: 0.01 }}
                />
                <TextField
                  label="מחיר"
                  type="number"
                  size="small"
                  inputRef={unitPriceRef}
                  value={productUnitPrice}
                  onChange={(e) => setProductUnitPrice(e.target.value)}
                  onFocus={(e) => e.target.select()}
                  onWheel={(e) => e.target.blur()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      // If Add is currently disabled because the product has no
                      // inventory in any storage, open the inline bump dialog so
                      // the user can keep going without breaking the keyboard loop.
                      if (storageEnabled && noInventoryWarning && selectedProduct) {
                        requestInventoryAdjust();
                        return;
                      }
                      addButtonRef.current?.focus();
                    }
                  }}
                  sx={{
                    minWidth: 110, direction: 'ltr',
                    '& input': {
                      '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                        WebkitAppearance: 'none', margin: 0,
                      },
                      MozAppearance: 'textfield',
                    },
                  }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">₪</InputAdornment>,
                  }}
                />
                {storageEnabled && availableStorages.length > 0 && (
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>מחסן</InputLabel>
                    <Select
                      label="מחסן"
                      value={selectedStorage?.StorageID || ''}
                      onChange={(e) => {
                        const s = availableStorages.find((x) => x.StorageID === e.target.value);
                        setSelectedStorage(s || null);
                      }}
                    >
                      {availableStorages.map((s) => (
                        <MenuItem key={s.StorageID} value={s.StorageID}>
                          {s.StorageName} ({s.available_quantity})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <Button
                  ref={addButtonRef}
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddProductRow}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddProductRow();
                    }
                  }}
                  disabled={!selectedProduct || (storageEnabled && noInventoryWarning)}
                  size="small"
                  sx={{ direction: 'ltr', alignSelf: 'center' }}
                >
                  הוסף
                </Button>
              </Box>

              {productEntries.length > 0 && (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>{productLabel}</TableCell>
                        {storageEnabled && <TableCell>מחסן</TableCell>}
                        <TableCell>כמות</TableCell>
                        <TableCell>מחיר יחידה</TableCell>
                        <TableCell>סה"כ</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {productEntries.map((entry, idx) => {
                        const price = parseFloat(entry.customPrice ?? entry.product.Price) || 0;
                        const lineTotal = price * (parseFloat(entry.quantity) || 0);
                        return (
                          <TableRow key={entry.entryId}>
                            <TableCell>{idx + 1}</TableCell>
                            <TableCell>{entry.product.ProductName}</TableCell>
                            {storageEnabled && <TableCell>{entry.storageName || '-'}</TableCell>}
                            <TableCell sx={{ p: 0.5 }}>
                              <TextField
                                type="number"
                                size="small"
                                value={entry.quantity}
                                onChange={(e) => handleUpdateEntry(idx, 'quantity', e.target.value)}
                                onFocus={(e) => e.target.select()}
                                onWheel={(e) => e.target.blur()}
                                inputProps={{ min: 0.01, step: 0.01 }}
                                sx={{
                                  width: 80,
                                  '& input': {
                                    textAlign: 'center',
                                    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                                      WebkitAppearance: 'none', margin: 0,
                                    },
                                    MozAppearance: 'textfield',
                                  },
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ p: 0.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                                <TextField
                                  type="number"
                                  size="small"
                                  value={entry.customPrice ?? entry.product.Price}
                                  onChange={(e) => handleUpdateEntry(idx, 'customPrice', e.target.value)}
                                  onFocus={(e) => e.target.select()}
                                  onWheel={(e) => e.target.blur()}
                                  InputProps={{
                                    endAdornment: <InputAdornment position="end">₪</InputAdornment>,
                                  }}
                                  sx={{
                                    width: 110, direction: 'ltr',
                                    '& input': {
                                      textAlign: 'center',
                                      '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                                        WebkitAppearance: 'none', margin: 0,
                                      },
                                      MozAppearance: 'textfield',
                                    },
                                  }}
                                />
                                {entry.customPrice != null && (
                                  <Tooltip title={`מחיר בסיס: ${formatCurrency(entry.product.Price)}`}>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ textDecoration: 'line-through', whiteSpace: 'nowrap' }}
                                    >
                                      {formatCurrency(entry.product.Price)}
                                    </Typography>
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>{formatCurrency(lineTotal)}</TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveProductEntry(idx)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          {/* Totals summary */}
          {productEntries.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardHeader
                title={
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="h6">סיכום</Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                      {selectedClient && (
                        <Chip
                          size="small"
                          color="primary"
                          variant="outlined"
                          icon={<PersonIcon />}
                          label={selectedClient.ClientName}
                          sx={{ direction: 'ltr' }}
                        />
                      )}
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`${productEntries.length} ${productEntries.length === 1 ? productLabel : productPluralLabel}`}
                      />
                    </Box>
                  </Box>
                }
              />
              <CardContent>
                {/* Per-line breakdown so the user sees exactly what they're about to charge */}
                <Box sx={{ mb: 2 }}>
                  {productEntries.map((entry, idx) => {
                    const basePrice = parseFloat(entry.product.Price) || 0;
                    const effectivePrice = parseFloat(entry.customPrice ?? entry.product.Price) || 0;
                    const qty = parseFloat(entry.quantity) || 0;
                    const lineTotal = effectivePrice * qty;
                    const overridden = entry.customPrice != null;
                    return (
                      <Box
                        key={entry.entryId}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: 0.5,
                          borderBottom: idx < productEntries.length - 1 ? '1px dashed' : 'none',
                          borderColor: 'divider',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minWidth: 22,
                              height: 22,
                              borderRadius: '50%',
                              bgcolor: 'action.selected',
                              color: 'text.secondary',
                              fontWeight: 'bold',
                            }}
                          >
                            {idx + 1}
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {entry.product.ProductName}
                          </Typography>
                          {entry.storageName && (
                            <Typography variant="caption" color="text.secondary">
                              ({entry.storageName})
                            </Typography>
                          )}
                          <Typography variant="body2" color="text.secondary">
                            {qty} × {formatCurrency(effectivePrice)}
                          </Typography>
                          {overridden && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ textDecoration: 'line-through' }}
                            >
                              {formatCurrency(basePrice)}
                            </Typography>
                          )}
                        </Box>
                        <Typography variant="body2" fontWeight="bold">
                          {formatCurrency(lineTotal)}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
                <Divider sx={{ mb: 1 }} />
                <Box sx={{ maxWidth: 360, mr: 'auto' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography color="text.secondary">סה"כ לפני הנחה:</Typography>
                    <Typography>{formatCurrency(formData.Subtotal)}</Typography>
                  </Box>
                  {(formData.CalculatedDiscount || 0) > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography color="text.secondary">
                        הנחה{discountType === 'percentage' ? ` (${parseFloat(formData.DiscountPercentage) || 0}%)` : ''}:
                      </Typography>
                      <Typography color="error">-{formatCurrency(formData.CalculatedDiscount)}</Typography>
                    </Box>
                  )}
                  {taxState !== 'none' && (
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography color="text.secondary">סכום לפני מע"מ:</Typography>
                        <Typography>{formatCurrency(formData.NetAmount)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography color="text.secondary">מע"מ ({formData.TaxRate}%):</Typography>
                        <Typography>{formatCurrency(formData.TaxAmount)}</Typography>
                      </Box>
                    </>
                  )}
                  {roundingEnabled && Math.abs(formData.RoundingAdjustment || 0) > 0.001 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography color="text.secondary">עיגול למטה:</Typography>
                      <Typography color="error">
                        {formatCurrency(formData.RoundingAdjustment)}
                      </Typography>
                    </Box>
                  )}
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6" fontWeight="bold">סה"כ לתשלום:</Typography>
                    <Typography variant="h6" fontWeight="bold">{formatCurrency(formData.TotalAmount)}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Payments — gated on PaidAmount being in ui_config.order_fields,
              matching OrderDetailPage so groups that don't expose PaidAmount
              don't see an inline capture UI here either. */}
          {allowOrderManagement && orderFields.includes('PaidAmount') && (
            <PaymentsSection
              payments={formData.payments}
              setPayments={(updater) =>
                setFormData((prev) => ({
                  ...prev,
                  payments: typeof updater === 'function' ? updater(prev.payments) : updater,
                }))
              }
              totalAmount={formData.TotalAmount}
            />
          )}

          {/* Submit */}
          {(() => {
            // Compute the reason the submit button is disabled, in priority
            // order, so the user always knows what's blocking them.
            let blockerReason = '';
            if (!selectedClient) {
              blockerReason = `יש לבחור לקוח לפני יצירת ה${orderLabel}`;
            } else if (productEntries.length === 0) {
              blockerReason = `יש להוסיף לפחות ${productLabel} אחד ל${orderLabel}`;
            }
            const disabled = isSubmitting || !!blockerReason;
            return (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                  mb: 4,
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                  onClick={handleSubmit}
                  disabled={disabled}
                  sx={{ minWidth: 220, direction: 'ltr' }}
                >
                  {isSubmitting ? 'יוצר...' : `צור ${orderLabel}`}
                </Button>
                {blockerReason && (
                  <Typography variant="caption" color="text.secondary">
                    {blockerReason}
                  </Typography>
                )}
              </Box>
            );
          })()}

          {/* Dialogs */}
          <ClientCreateDialog
            open={createClientDialogOpen}
            onClose={() => setCreateClientDialogOpen(false)}
            onClientCreated={(newClient) => {
              if (newClient) {
                handleClientSelect(null, newClient);
              }
              setCreateClientDialogOpen(false);
              queryClient.refetchQueries(['clients', 'autocomplete']);
            }}
            userProfile={userProfile}
          />

          <ProductCreateInline
            open={createProductDialogOpen}
            onClose={() => setCreateProductDialogOpen(false)}
            productLabel={productLabel}
            productFields={productFields}
            productFieldsSchema={productFieldsSchema}
            storageEnabled={storageEnabled}
            allStorages={allStorages}
            onCreated={handleProductCreated}
          />

          {barcodeEnabled && (
            <BarcodeScannerDialog
              open={scannerOpen}
              onClose={() => setScannerOpen(false)}
              onAdd={handleScannedProductAdd}
              productLabel={productLabel}
            />
          )}

          {/*
            Reuses the same InventoryManagementDialog as ProductsPage — it has
            the take/add UX where the user types HOW MUCH to add (not the new
            absolute total). This is the dialog the user explicitly asked for.
          */}
          <InventoryManagementDialog
            open={inventoryAdjustOpen}
            onClose={() => setInventoryAdjustOpen(false)}
            product={selectedProduct}
            storages={allStorages}
            initialStorageId={
              selectedStorage?.StorageID ||
              allStorages.find((s) => s.is_default)?.StorageID ||
              allStorages[0]?.StorageID ||
              null
            }
            onSuccess={async () => {
              if (!selectedProduct) return;
              try {
                const refreshed = await fetchProductDetails(selectedProduct.ProductID);
                handleInventoryAdjusted(refreshed);
              } catch (e) {
                setProductsCardError('שגיאה ברענון פרטי המוצר');
              }
            }}
            canTakeInventory={canTakeInventory}
            canAddInventory={canAddInventory}
          />

          {/* Draft restore */}
          <MobileDialog open={draftDialogOpen} onClose={handleDiscardDraft} maxWidth="xs" fullWidth>
            <DialogTitle>שחזור טיוטה</DialogTitle>
            <DialogContent>
              <Typography sx={{ mb: 2 }}>
                נמצאה טיוטה שמורה של {orderLabel} שלא הושלמה. האם לשחזר אותה?
              </Typography>
              {pendingDraft && (
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <PersonIcon fontSize="small" color="primary" />
                    <Typography variant="body2">
                      <strong>לקוח:</strong>{' '}
                      {pendingDraft.selectedClient?.ClientName || <em>(לא נבחר)</em>}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <ShoppingCartIcon fontSize="small" color="primary" />
                    <Typography variant="body2">
                      <strong>{productPluralLabel}:</strong>{' '}
                      {(pendingDraft.productEntries?.length ?? 0)}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    נשמרה ב־{new Date(pendingDraft.savedAt).toLocaleString('he-IL')}
                  </Typography>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleDiscardDraft}>מחק טיוטה</Button>
              <Button variant="contained" onClick={handleRestoreDraft}>שחזר</Button>
            </DialogActions>
          </MobileDialog>
        </Container>
      </LocalizationProvider>
    </ThemeProvider>
  );
};

export default NewOrderPage;
